import { useMemo } from 'react';

import DonutLargeOutlined from '@mui/icons-material/DonutLargeOutlined';
import ShowChart from '@mui/icons-material/ShowChart';
import {
  Alert,
  alpha,
  Box,
  Card,
  CardContent,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import FicFidcConcentrationCurve from 'src/components/FicFidcConcentrationCurve';
import { convertValue, formatPercentageValue } from 'src/utils/number';

import {
  CONCENTRATION_RANKS,
  getConcentrationPointAtRank,
  mapFicFidcConcentration,
} from './mapFicFidcConcentration';

import type {
  MetaCarteirasFicFidcsChildPosition,
  MetaCarteirasFicFidcsChildrenMeta,
} from 'src/services/meta-carteiras-fic-fidcs';

export { getConcentrationMarkerLabelPosition } from 'src/components/FicFidcConcentrationCurve';

interface FicFidcConcentrationChartProps {
  positions: MetaCarteirasFicFidcsChildPosition[];
  meta?: MetaCarteirasFicFidcsChildrenMeta;
  isLoading?: boolean;
  isError?: boolean;
}

function formatCurrency(value: number) {
  return convertValue(value, '', true, 'currency', 'BRL', 1, 0);
}

function formatBaseMonth(baseMonth?: string) {
  if (!baseMonth) return null;

  try {
    return format(parseISO(baseMonth), 'MM/yyyy', { locale: ptBR });
  } catch {
    return null;
  }
}

function ConcentrationCard({
  rank,
  percentage,
  accumulatedValue,
  highlighted = false,
}: {
  rank: number;
  percentage: number | null;
  accumulatedValue: number | null;
  highlighted?: boolean;
}) {
  const hasValue = percentage !== null && accumulatedValue !== null;
  const label = `TOP ${rank} ${rank === 1 ? 'FIDC' : 'FIDCS'}`;

  return (
    <Card
      aria-label={`${label}: ${hasValue ? formatPercentageValue(percentage, 1) : 'N/A'}`}
      data-highlighted={highlighted}
      data-state={hasValue ? 'available' : 'unavailable'}
      data-testid={`fic-fidc-concentration-card-${rank}`}
      variant="outlined"
      sx={(theme) => ({
        backgroundColor: hasValue
          ? highlighted
            ? alpha(theme.palette.primary.main, 0.04)
            : theme.palette.background.paper
          : theme.palette.disabled.main,
        borderColor: highlighted
          ? alpha(theme.palette.primary.main, 0.55)
          : theme.palette.divider,
        borderRadius: 2,
        boxShadow: theme.shadows[1],
        height: '100%',
      })}>
      <CardContent
        sx={{
          '&:last-child': { pb: 2 },
          minHeight: 116,
          p: { xs: 1.75, sm: 2 },
        }}>
        <Typography
          color="text.secondary"
          sx={{ fontWeight: 600, letterSpacing: 0.5 }}
          variant="caption">
          {label}
        </Typography>
        <Typography
          color={hasValue ? 'text.primary' : 'text.disabled'}
          fontWeight={700}
          sx={{ mt: 1 }}
          variant="h5">
          {hasValue ? formatPercentageValue(percentage, 1) : 'N/A'}
        </Typography>
        <Typography
          color={hasValue ? 'text.secondary' : 'text.disabled'}
          sx={{ minHeight: 21 }}
          variant="body2">
          {hasValue
            ? `${formatCurrency(accumulatedValue)} da carteira`
            : 'Valor indisponível'}
        </Typography>
      </CardContent>
    </Card>
  );
}

function ConcentrationLoading() {
  return (
    <Box aria-label="Carregando concentração da carteira">
      <Grid container spacing={2}>
        {CONCENTRATION_RANKS.map((rank) => (
          <Grid item key={rank} md={3} sm={6} xs={12}>
            <Card
              variant="outlined"
              sx={(theme) => ({
                borderColor: theme.palette.divider,
                borderRadius: 2,
                boxShadow: theme.shadows[1],
              })}>
              <CardContent sx={{ minHeight: 116, p: { xs: 1.75, sm: 2 } }}>
                <Skeleton height={18} width="45%" />
                <Skeleton height={34} sx={{ mt: 1 }} width="55%" />
                <Skeleton height={21} width="70%" />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Paper
        variant="outlined"
        sx={(theme) => ({
          borderColor: theme.palette.divider,
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          mt: 4,
          p: { xs: 2, md: 3 },
        })}>
        <Skeleton height={26} width="35%" />
        <Skeleton height={20} sx={{ mt: 0.5 }} width="65%" />
        <Skeleton height={320} sx={{ mt: 2 }} variant="rounded" />
      </Paper>
    </Box>
  );
}

export default function FicFidcConcentrationChart({
  positions,
  meta,
  isLoading = false,
  isError = false,
}: FicFidcConcentrationChartProps) {
  const concentration = useMemo(
    () => mapFicFidcConcentration(positions, meta),
    [meta, positions],
  );
  const baseMonth = formatBaseMonth(meta?.baseMonth ?? positions[0]?.dtComptc);

  return (
    <Box
      aria-label="Concentração da Carteira"
      component="section"
      mt={3}
      sx={{ width: '100%' }}>
      <Stack spacing={0.75} sx={{ mb: 2.5 }}>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'space-between',
          }}>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            <DonutLargeOutlined
              aria-hidden
              sx={{ color: 'primary.main', fontSize: 21 }}
            />
            <Typography component="h2" fontWeight={700} variant="h6">
              Concentração da Carteira
            </Typography>
          </Box>
          {baseMonth && (
            <Typography fontWeight={700} variant="body2">
              Mês base: {baseMonth}
            </Typography>
          )}
        </Box>
        <Typography color="text.secondary" sx={{ mt: 2 }} variant="body2">
          Participação acumulada dos maiores FIDCs detidos pela carteira sobre o
          total investido.
        </Typography>
      </Stack>

      {isError ? (
        <Alert severity="error" variant="outlined">
          Não foi possível carregar a concentração da carteira.
        </Alert>
      ) : isLoading ? (
        <ConcentrationLoading />
      ) : concentration.status === 'empty' ? (
        <Alert severity="info" variant="outlined">
          Não há dados disponíveis para calcular a concentração da carteira.
        </Alert>
      ) : concentration.status === 'partial' ? (
        <Alert severity="warning" variant="outlined">
          Os dados das posições estão incompletos. A concentração da carteira
          está indisponível no momento.
        </Alert>
      ) : concentration.status === 'unavailable' ? (
        <Alert severity="info" variant="outlined">
          Não foi possível calcular a concentração da carteira com os dados
          disponíveis.
        </Alert>
      ) : (
        <>
          <Grid container alignItems="stretch" spacing={2}>
            {CONCENTRATION_RANKS.map((rank) => {
              const point = getConcentrationPointAtRank(concentration, rank);

              return (
                <Grid item key={rank} md={3} sm={6} xs={12}>
                  <ConcentrationCard
                    accumulatedValue={point?.accumulatedValue ?? null}
                    highlighted={rank === 1}
                    percentage={point?.accumulatedPercentage ?? null}
                    rank={rank}
                  />
                </Grid>
              );
            })}
          </Grid>
          <Paper
            data-testid="fic-fidc-concentration-panel"
            variant="outlined"
            sx={(theme) => ({
              borderColor: theme.palette.divider,
              borderRadius: 2,
              boxShadow: theme.shadows[1],
              mt: 4,
              p: { xs: 2, md: 3 },
              width: '100%',
            })}>
            <Stack spacing={0.5}>
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                <ShowChart
                  aria-hidden
                  sx={{ color: 'primary.main', fontSize: 20 }}
                />
                <Typography component="h3" fontWeight={700} variant="h6">
                  Curva de concentração
                </Typography>
              </Box>
              <Typography color="text.secondary" variant="body2">
                % acumulado da carteira × número de FIDCs, ordenados do maior
                para o menor ({concentration.totalFunds}{' '}
                {concentration.totalFunds === 1 ? 'fundo' : 'fundos'}).
              </Typography>
            </Stack>
            <FicFidcConcentrationCurve
              points={concentration.points}
              totalFunds={concentration.totalFunds}
            />
          </Paper>
        </>
      )}
    </Box>
  );
}

export type { FicFidcConcentrationChartProps };
